import { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import { Paper, Typography, FormGroup, FormControlLabel, Checkbox, Box } from '@mui/material';

// Register required elements for Line Chart
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement, // Required for Line Chart
  LineElement, // Required for Line Chart
  Tooltip,
  Legend
);

export const OrderMonthlyLineChart = ({ data }) => {
  // State to track which statuses are visible
  const [visibleStatuses, setVisibleStatuses] = useState({
    pending: true,
    processing: true,
    received: true,
    rejected: true
  });

  // Get unique months and statuses
  const months = [...new Set(data.map(item => item.day.trim()))];
  const statuses = ['pending', 'processing', 'received', 'rejected']; // Predefined statuses for consistency

  // Group data by month and status
  const groupedData = months.map(month => {
    const monthData = data.filter(item => item.day.trim() === month);
    return {
      month,
      ...statuses.reduce((acc, status) => {
        acc[status] = monthData
          .filter(item => item.status === status)
          .reduce((sum, curr) => sum + curr.totalAmount, 0);
        return acc;
      }, {})
    };
  });

  // Define colors for each status
  const statusColors = {
    pending: '#FFC107',
    processing: '#17A2B8',
    received: '#28A745',
    rejected: '#DC3545'
  };

  // Filter datasets based on visible statuses
  const filteredDatasets = statuses
    .filter(status => visibleStatuses[status]) // Only include visible statuses
    .map(status => ({
      label: status.toUpperCase(),
      data: groupedData.map(item => item[status]),
      borderColor: statusColors[status],
      backgroundColor: `${statusColors[status]}33`, // Add transparency
      fill: false,
      tension: 0.4 // Smooth line
    }));

  // Prepare chart data
  const chartData = {
    labels: groupedData.map(item => item.month),
    datasets: filteredDatasets
  };

  // Chart options
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#000000' // Legend text color
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.dataset.label || '';
            const value = context.raw || 0;
            return `${label}: $${value.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value}`,
          color: '#000000' // Y-axis text color
        },
        grid: {
          color: '#E0E0E0' // Y-axis grid color
        }
      },
      x: {
        ticks: {
          color: '#000000' // X-axis text color
        },
        grid: {
          color: '#E0E0E0' // X-axis grid color
        }
      }
    },
    maintainAspectRatio: false
  };

  // Handle status toggle
  const handleStatusToggle = (status) => {
    setVisibleStatuses((prev) => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 3, backgroundColor: 'background.paper' }}>
      <Typography variant="h6" gutterBottom>
      Total Amount Over Months by Status
      </Typography>

      {/* Status Filter Checkboxes */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: 'background.default', borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
          Filter by Status
        </Typography>
        <FormGroup row>
          {statuses.map(status => (
            <FormControlLabel
              key={status}
              control={
                <Checkbox
                  checked={visibleStatuses[status]}
                  onChange={() => handleStatusToggle(status)}
                  color="primary"
                  sx={{
                    color: statusColors[status],
                    '&.Mui-checked': {
                      color: statusColors[status],
                    },
                  }}
                />
              }
              label={status.toUpperCase()}
              sx={{ color: 'text.primary' }}
            />
          ))}
        </FormGroup>
      </Box>

      {/* Line Chart with Margin End */}
      <Box sx={{ height: '400px', mr: 3 }}> {/* Added margin end (mr: 3) */}
        <Line data={chartData} options={options} />
      </Box>
    </Paper>
  );
};